import { useCallback, useState, useEffect, useRef } from 'react';
import * as unifiedAlertsService from 'service/unifiedAlerts';
import { FilterState } from '../AdvancedFilterDrawer/types';
import { DEFAULT_FILTERS } from '../AdvancedFilterDrawer/constants';
import { timeRangeToDates, transformTrendData } from '../unifiedAlertsUtils';
import { loadDashboardFilters, saveDashboardFilters } from 'service/service';
import { useSearchParams } from 'react-router-dom';
import { GenericOption } from '../AdvancedFilterDrawer/FilterMultiSelect';
import type { SortModel } from 'components/ColumnFilters/types';
import {
  ColumnFilterPayloadItem,
  getSortingFromSortModel,
  loadColumnFilterPayload,
  saveColumnFilterPayload,
} from '../columnFilterUtils';

export const FILTER_RELATIONSHIP_FIELD_MAP = {
  department: 'Department',
  capability: 'Capability',
  sector: 'Sector',
  domains: 'Domain',
  programs: 'Program',
  businessUnits: 'Business Unit',
  assignmentGroup: 'Assignment Group',
  assignmentGroupListName: 'Assignment Group List',
  vendor: 'Vendor',
  team: 'Team',
  subCapability: 'Sub_Track',
  technology: 'Technology',
} as const; // severity, status, priority, sources, cmdb_ci not there here because fullMap doesn't contain those relationships.

const DATA_QUALITY_COLUMN_FILTER_VALUES = [true, false];

const EWS_COLUMN_FILTER_OPTION_KEYS: Record<string, string> = {
  source: 'sources',
  mappedSeverity: 'mappedSeverity',
  sn_priority_group: 'priority',
  state: 'status',
  domain: 'alertDomain',
  cmdb_ci: 'cmdbCi',
  sector: 'alertSector',
  department: 'alertDepartment',
  program: 'alertProgram',
  businessUnit: 'alertBusinessUnit',
  capability: 'alertCapability',
};

const DEFAULT_FILTER_OPTIONS = {
  severity: ['Critical', 'Major', 'Minor', 'Warning'],
  status: ['Open', 'Reopen', 'Flapping', 'Closed'],
  sector: [],
  programs: [],
  domains: [],
  businessUnits: [],
  sources: [],
  assignmentGroup: [],
  sysDomains: [],
  cmdbCi: [],
  capability: [],
  department: [],
  priority: ['Urgent', 'High', 'Moderate', 'Low'],
  alertCategory: ['ALL', 'Pipeline', 'Data Quality'],
};

export const getSelectedFilterValues = (options?: GenericOption[]) => {
  return (options || [])
    .map(option => option?.value)
    .filter(value => value !== undefined && value !== null && value !== '')
    .map(String);
};

export const getCascadingFilterOptions = (
  currentFilters: FilterState,
  records: any[],
  miscellaneousSectors: string[] = [],
  options?: {
    ignoreDerivedAssignmentGroup?: boolean;
  }
) => {
  const result: Record<string, string[]> = {};

  const filterKeys = Object.keys(
    FILTER_RELATIONSHIP_FIELD_MAP
  ) as Array<keyof typeof FILTER_RELATIONSHIP_FIELD_MAP>;

  /*
   * Check whether at least one relationship-based filter
   * has been selected.
   *
   * If nothing is selected, return an empty result so that
   * the caller continues using the original dropdown options.
   */
  const hasRelationshipFilters = filterKeys.some(key => {
    if (
      key === 'assignmentGroup' &&
      options?.ignoreDerivedAssignmentGroup
    ) {
      return false;
    }

    const selectedValues =
      currentFilters[key] as GenericOption[] | undefined;

    return getSelectedFilterValues(selectedValues).length > 0;
  });

  // Nothing selected → don't override original dropdown options
  if (!hasRelationshipFilters) {
    return result;
  }

  filterKeys.forEach(currentFilterKey => {
    /*
     * When calculating the options for the current field,
     * ignore that field's own selection.
     *
     * Example:
     *
     * Department = IT
     * Sector = Miscellaneous
     *
     * When calculating Sector options, Sector itself is ignored
     * and Department = IT is used to determine available sectors.
     */

    const otherFilterKeys = filterKeys.filter(key => {
      if (
        key === 'assignmentGroup' &&
        options?.ignoreDerivedAssignmentGroup
      ) {
        return false;
      }

      return key !== currentFilterKey;
    });

    /*
     * Find relationship records that satisfy all OTHER selected
     * filters.
     *
     * Across different fields → AND
     * Within the same field → OR
     */
    const matchingRecords = records.filter(record => {
      return otherFilterKeys.every(filterKey => {
        const selectedValues = getSelectedFilterValues(
          currentFilters[filterKey] as GenericOption[]
        );

        // Nothing selected in this filter →
        // it should not restrict the result.
        if (selectedValues.length === 0) {
          return true;
        }

        const recordField =
          FILTER_RELATIONSHIP_FIELD_MAP[filterKey];

        const recordValue = record?.[recordField];

        if (
          recordValue === undefined ||
          recordValue === null ||
          recordValue === ''
        ) {
          return false;
        }

        /*
         * Normalize the relationship record value into an array.
         *
         * This allows the same logic to handle:
         *
         * Sector: "Technology"
         *
         * and:
         *
         * Sector: ["Technology", "Cloud"]
         */
        const recordValues = Array.isArray(recordValue)
          ? recordValue.map(String)
          : [String(recordValue)];

        /*
         * Check whether the current relationship record matches
         * ANY selected value in this filter.
         *
         * This gives OR semantics within a filter.
         */
        return selectedValues.some(selectedValue => {
          /*
           * SPECIAL CASE:
           *
           * "Miscellaneous" is a synthetic Sector value.
           *
           * The relationship records do NOT contain:
           *
           *     Sector = "Miscellaneous"
           *
           * Instead they contain the actual miscellaneous sectors:
           *
           *     Russia
           *     AMESA APAC
           *     CTO
           *     DPS
           *     ...
           *
           * Therefore:
           *
           *     Sector = Miscellaneous
           *
           * means:
           *
           *     Sector IN miscellaneousSectors
           */
          if (
            filterKey === 'sector' &&
            selectedValue === 'Miscellaneous'
          ) {
            return recordValues.some(value =>
              miscellaneousSectors.includes(value)
            );
          }

          // Normal relationship matching
          return recordValues.includes(selectedValue);
        });
      });
    });

    /*
     * Now collect the possible values for the field
     * we are currently calculating.
     */
    const targetField =
      FILTER_RELATIONSHIP_FIELD_MAP[currentFilterKey];

    const values = new Set<string>();

    matchingRecords.forEach(record => {
      const value = record?.[targetField];

      const recordValues = Array.isArray(value)
        ? value
        : [value];

      recordValues.forEach(item => {
        if (
          item === null ||
          item === undefined ||
          item === ''
        ) {
          return;
        }

        const stringValue = String(item);

        /*
         * SPECIAL CASE:
         *
         * The relationship records contain the real miscellaneous
         * sector values, but the UI represents all of them as one
         * synthetic Sector option:
         *
         *     Miscellaneous
         *
         * Therefore, when calculating Sector options, collapse:
         *
         *     Russia
         *     CTO
         *     DPS
         *     ...
         *
         * into:
         *
         *     Miscellaneous
         */
        if (
          currentFilterKey === 'sector' &&
          miscellaneousSectors.includes(stringValue)
        ) {
          values.add('Miscellaneous');
        } else {
          values.add(stringValue);
        }
      });
    });

    result[currentFilterKey] =
      Array.from(values).sort();
  });

  return result;
};


export const reconcileCascadingFilters = (
  currentFilters: FilterState,
  cascadingOptions: Record<string, string[]>
): FilterState => {
  let hasChanges = false;

  const nextFilters = { ...currentFilters };

  (
    Object.keys(FILTER_RELATIONSHIP_FIELD_MAP) as Array<
      keyof typeof FILTER_RELATIONSHIP_FIELD_MAP
    >
  )
    .filter(key => key !== 'sector')
    .forEach(key => {
      const allowedValues = cascadingOptions[key];

      // No cascading restriction for this field.
      if (allowedValues === undefined) {
        return;
      }

      const selectedOptions =
        currentFilters[key] as GenericOption[] | undefined;

      if (!selectedOptions?.length) {
        return;
      }

      const allowedSet = new Set(
        allowedValues.map(String)
      );

      const validSelections = selectedOptions.filter(option => {
        const value = String(option.value);

        /*
         * SPECIAL CASE:
         *
         * "Miscellaneous" is a synthetic Sector option.
         *
         * It does not exist as a real Sector value in the
         * relationship records. The real values are stored in:
         *
         * filterOptions.sectorDropdownValues.Miscellaneous
         *
         * Therefore, never remove "Miscellaneous" simply because
         * the cascading relationship engine cannot find the
         * literal value "Miscellaneous".
         */
        // if (
        //   key === 'sector' &&
        //   value === 'Miscellaneous'
        // ) {
        //   return true;
        // }

        return allowedSet.has(value);
      });

      if (validSelections.length !== selectedOptions.length) {
        hasChanges = true;

        nextFilters[key] = validSelections as never;
      }
    });

  return hasChanges ? nextFilters : currentFilters;
};

export const getDerivedAssignmentGroups = (
  currentFilters: FilterState,
  records: any[],
): string[] => {
  const sourceKeys: Array<
    'vendor' | 'team' | 'assignmentGroupListName' | 'subCapability' | 'technology'
  > = [
      'vendor',
      'team',
      'subCapability',
      'technology',
      'assignmentGroupListName'
    ];

  const activeSourceKeys = sourceKeys.filter(key => {
    const selectedValues = getSelectedFilterValues(
      currentFilters[key] as GenericOption[] | undefined
    );

    return selectedValues.length > 0;
  });

  // Nothing is driving Assignment Group.
  if (!activeSourceKeys.length) {
    return [];
  }

  /*
   * For each source field:
   *
   * Vendor:
   *   V1 OR V2
   *
   * Team:
   *   T1 OR T2
   *
   * Assignment Group List:
   *   L1 OR L2
   *
   * Across fields:
   *
   * Vendor AND Team AND List
   */

  const assignmentGroupSets = activeSourceKeys.map(sourceKey => {
    const selectedValues = getSelectedFilterValues(
      currentFilters[sourceKey] as GenericOption[]
    );

    const relationshipField =
      FILTER_RELATIONSHIP_FIELD_MAP[sourceKey];

    const matchingGroups = new Set<string>();

    records.forEach(record => {
      const recordValue = record?.[relationshipField];

      if (
        recordValue === undefined ||
        recordValue === null ||
        recordValue === ''
      ) {
        return;
      }

      const recordValues = Array.isArray(recordValue)
        ? recordValue.map(String)
        : [String(recordValue)];

      const matches = selectedValues.some(selectedValue =>
        recordValues.includes(String(selectedValue))
      );

      if (!matches) {
        return;
      }

      const assignmentGroup = record?.['Assignment Group'];

      if (
        assignmentGroup === undefined ||
        assignmentGroup === null ||
        assignmentGroup === ''
      ) {
        return;
      }

      matchingGroups.add(String(assignmentGroup));
    });

    return matchingGroups;
  });

  /*
   * AND across Vendor / Team / Assignment Group List.
   */
  if (!assignmentGroupSets.length) {
    return [];
  }

  const [firstSet, ...remainingSets] = assignmentGroupSets;

  return Array.from(firstSet).filter(group =>
    remainingSets.every(set => set.has(group))
  );
};

export const expandSectorFilterFlat = (
  params: Record<string, any>,
  filterOptions: any
) => {
  const sectorMap = filterOptions?.sectorDropdownValues || {};

  const selectedSector = params?.sector;

  if (!selectedSector) {
    return params;
  }

  const sectorList = Array.isArray(selectedSector)
    ? selectedSector
    : String(selectedSector)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  const selectedMiscellaneous =
    params?.miscellaneousSector || [];

  const expandedList = sectorList.reduce(
    (acc: string[], sector: string) => {
      if (sector === 'Miscellaneous') {
        return acc.concat(selectedMiscellaneous);
      }

      const directMapped = sectorMap[sector];

      if (Array.isArray(directMapped)) {
        return acc.concat(directMapped);
      }

      const reverseMapped = Object.entries(sectorMap).find(
        ([_, mappedValues]: any) =>
          Array.isArray(mappedValues) &&
          mappedValues.includes(sector)
      );

      if (reverseMapped) {
        return acc.concat(reverseMapped[1] as string[]);
      }

      return acc.concat(sector);
    },
    []
  );

  const uniqueList = expandedList.filter(
    (item: any, index: number, arr: any) => arr.indexOf(item) === index
  );

  const {
    miscellaneousSector,
    ...restParams
  } = params;

  return {
    ...restParams,
    sector: uniqueList.join(','),
  };
};


type DomainInput = string | string[];

const DOMAIN_MAP: Record<string, string[]> = {
  Others: ['Others', 'Other'],
  Global: ['Global', 'Global/Supply Chain', 'Supply Chain'],
  Consumer: [
    'Consumer',
    'Consumer & Suppy Chain',
    'Consumer & Supply Chain'
  ]
};

const PROGRAM_MAP: Record<string, string[]> = {
  Unassigned: ['NA', 'Unassigned', "FP&A"],
};

export const expandMappedValues = (
  input: DomainInput,
  map: Record<string, string[]>
): string => {
  if (!input) return '';

  const values = Array.isArray(input) ? input : [input];

  const result: string[] = [];

  values.forEach((value) => {
    let matched = false;

    for (const [groupKey, mappedValues] of Object.entries(map)) {
      if (groupKey === value || mappedValues.includes(value)) {
        result.push(groupKey, ...mappedValues);
        matched = true;
        break;
      }
    }

    // preserve original if no match
    if (!matched) {
      result.push(value);
    }
  });

  return Array.from(new Set(result)).join(', ');
};

const EWS_COLUMN_FILTER_STORAGE_KEY = 'unifiedAlerts_column_filter_state';
const DEFAULT_ALERT_SORTING = { sortBy: 'last_event_time', sortDir: 'DESC' };

type SortValues = Array<string | number>;

interface AlertPageRequest {
  searchCursor: SortValues | null;
  direction: 'next' | 'prev';
}

interface AlertPageCursor {
  signature: string;
  page: number;
  firstSortValues: SortValues | null;
  lastSortValues: SortValues | null;
  request: AlertPageRequest;
}

const FIRST_ALERT_PAGE_REQUEST: AlertPageRequest = { searchCursor: null, direction: 'next' };

// The alerts list is cursor paginated: Next continues after the last row, Prev continues before the first row.
// A cursor is only valid for the query that produced it, so any change to that query restarts from page one (null).
const getAlertPageRequest = (
  cursor: AlertPageCursor | null,
  signature: string,
  targetPage: number
): AlertPageRequest | null => {
  if (targetPage === 1) return FIRST_ALERT_PAGE_REQUEST;
  if (!cursor || cursor.signature !== signature) return null;
  if (targetPage === cursor.page + 1 && cursor.lastSortValues) {
    return { searchCursor: cursor.lastSortValues, direction: 'next' };
  }
  if (targetPage === cursor.page - 1 && cursor.firstSortValues) {
    return { searchCursor: cursor.firstSortValues, direction: 'prev' };
  }
  if (targetPage === cursor.page) return cursor.request;
  return null;
};

// The header picker stores custom end times at minute precision (e.g. 23:59:00) and the API filters with lte,
// so extend the end to the last millisecond of that minute to include the whole selected end minute.
const toEndOfMinute = (iso: string) => {
  const date = new Date(iso);
  date.setSeconds(59, 999);
  return date.toISOString();
};

export const useUnifiedAlerts = () => {
  const [activeTile, setActiveTile] = useState('Critical');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [alertCountView, setAlertCountView] = useState<'overall' | 'unique'>('overall');
  const [simpleSearchText, setSimpleSearchText] = useState('');
  const [sortingInfo, setSortingInfo] = useState(DEFAULT_ALERT_SORTING);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState<any>(DEFAULT_FILTER_OPTIONS);
  const filterOptionsRef = useRef<any>(DEFAULT_FILTER_OPTIONS);
  useEffect(() => {
    filterOptionsRef.current = filterOptions;
  }, [filterOptions]);
  const [filterRelationshipRecords, setFilterRelationshipRecords] =
    useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [isDataOpsAdmin, setIsDataOpsAdmin] = useState<boolean>(false);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [trendSector, setTrendSector] = useState<any>(null);
  const [trendProgram, setTrendProgram] = useState<any>(null);
  const [sources, setSources] = useState<any[]>([]);
  const [mostImpactedApps, setMostImpactedApps] = useState<any[]>([]);
  const [severityData, setSeverityData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isChartsLoading, setIsChartsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isFiltersHydrated, setIsFiltersHydrated] = useState(false);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number | null>(() => {
    const saved = sessionStorage.getItem('unifiedAlertsAutoRefresh');
    return saved ? Number(saved) : 5;
  });
  const [fixedStartDate, setFixedStartDate] = useState<string | undefined>(undefined);
  const [fixedEndDate, setFixedEndDate] = useState<string | undefined>(undefined);
  const [groupBy, setGroupBy] = useState<string>('none');
  const [isGrouped, setIsGrouped] = useState(false);
  const [searchAfter, setSearchAfter] = useState<any>(null);
  const [isLoadingMoreChildren, setIsLoadingMoreChildren] = useState(false);
  const [loadingGroupId, setLoadingGroupId] = useState<string | null>(null);
  const [columnFilters, setColumnFilters] = useState<ColumnFilterPayloadItem[]>(
    () => loadColumnFilterPayload(EWS_COLUMN_FILTER_STORAGE_KEY)
  );
  const [searchParams] = useSearchParams();
  const ignoreDate = searchParams.get('ignoreDate') === 'true';

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  // Auto-refresh and date-range listeners hold older loadAlerts closures, so they read column filters from a ref
  const columnFiltersRef = useRef<ColumnFilterPayloadItem[]>(columnFilters);
  columnFiltersRef.current = columnFilters;
  const summaryRequestRef = useRef<{ key: string; promise: Promise<any> } | null>(null);
  const alertPageCursorRef = useRef<AlertPageCursor | null>(null);
  const initializationStartedRef = useRef(false);

  const persistDateRangeToSession = (filters: FilterState) => {
    if (filters.timeRange) {
      sessionStorage.setItem('unifiedAlertsDateRangeType', filters.timeRange);
    }

    if (filters.timeRange === 'custom' && filters.startDate && filters.endDate) {
      sessionStorage.setItem('unifiedAlertsStartDate', filters.startDate);
      sessionStorage.setItem('unifiedAlertsEndDate', filters.endDate);
    } else {
      sessionStorage.removeItem('unifiedAlertsStartDate');
      sessionStorage.removeItem('unifiedAlertsEndDate');
    }
    window.dispatchEvent(new Event('unifiedAlertsDateRangeHydrated'));
  };

  const withCustomDates = (filters: FilterState): FilterState => {
    if (filters.timeRange !== 'custom') return filters;

    return {
      ...filters,
      startDate: filters.startDate ?? sessionStorage.getItem('unifiedAlertsStartDate') ?? undefined,
      endDate: filters.endDate ?? sessionStorage.getItem('unifiedAlertsEndDate') ?? undefined,
    };
  };

  const getDateParams = (): { startDate?: string; endDate?: string } => {
    const pewsType = sessionStorage.getItem('unifiedAlertsDateRangeType');

    if (pewsType) {
      if (pewsType === 'more_than_90_days') {
        const end = sessionStorage.getItem('unifiedAlertsEndDate');

        if (end) {
          return { endDate: end };
        }

        const olderThan90End = new Date();
        olderThan90End.setDate(olderThan90End.getDate() - 90);

        return {
          endDate: olderThan90End.toISOString(),
        };
      }

      if (pewsType === 'custom') {
        const start = sessionStorage.getItem('unifiedAlertsStartDate');
        const end = sessionStorage.getItem('unifiedAlertsEndDate');
        if (start && end) return { startDate: start, endDate: toEndOfMinute(end) };
      } else {
        // Local helper — does NOT use startOf('day'), just exact current moment
        const now = new Date();
        const endDate = now.toISOString();
        const parseValue = (v: string) => {
          const match = v.match(/^(\d+)\s*(m|min|minutes|h|hr|hour|d|days|day|w|weeks|week)$/i);
          if (!match) return null;
          const num = parseInt(match[1]);
          const unit = match[2].toLowerCase();


          let multiplier = 1;
          if (unit.startsWith('m') && !unit.startsWith('mi')) multiplier = 60 * 1000;
          else if (unit.startsWith('mi')) multiplier = 60 * 1000;
          else if (unit.startsWith('h')) multiplier = 60 * 60 * 1000;
          else if (unit.startsWith('d')) multiplier = 24 * 60 * 60 * 1000;
          else if (unit.startsWith('w')) multiplier = 7 * 24 * 60 * 60 * 1000;

          return num * multiplier;
        };
        const rangeMap: Record<string, number> = {
          '5m': 5 * 60 * 1000,
          '10m': 10 * 60 * 1000,
          '15m': 15 * 60 * 1000,
          '30m': 30 * 60 * 1000,
          '1h': 60 * 60 * 1000,
          '24h': 24 * 60 * 60 * 1000,
          '7d': 7 * 24 * 60 * 60 * 1000,
          '30d': 30 * 24 * 60 * 60 * 1000,
          '90d': 90 * 24 * 60 * 60 * 1000,
          'today': now.getTime() - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime(),
        };
        const offset = rangeMap[pewsType] ?? parseValue(pewsType);
        if (offset !== undefined && offset !== null) {
          return {
            startDate: new Date(now.getTime() - offset).toISOString(),
            endDate,
          };
        }
      }
    }
    if (
      appliedFilters.timeRange === 'custom' &&
      appliedFilters.startDate &&
      appliedFilters.endDate
    ) {
      return {
        startDate: appliedFilters.startDate,
        endDate: toEndOfMinute(appliedFilters.endDate),
      };
    }

    return timeRangeToDates(appliedFilters.timeRange);
  };

  const isOlderThan90DaysSelected = () => {
    const sessionRange = sessionStorage.getItem('unifiedAlertsDateRangeType');

    if (sessionRange) {
      return sessionRange === 'more_than_90_days';
    }

    return appliedFilters.timeRange === 'more_than_90_days';
  };

  const getDatePayloadParams = (dateParams?: { startDate?: string; endDate?: string }) => {
    const resolvedDateParams = dateParams ?? getDateParams();

    if (isOlderThan90DaysSelected()) {
      return {
        endDate: resolvedDateParams.endDate || getDateParams().endDate,
      };
    }

    return {
      startDate: resolvedDateParams.startDate || getDateParams().startDate,
      endDate: resolvedDateParams.endDate || getDateParams().endDate,
    };
  };
  useEffect(() => {
    const handleDateRangeChanged = () => {
      loadAll(getDateParams());
      loadAlerts(getDateParams());
    };

    window.addEventListener('unifiedAlertsDateRangeChanged', handleDateRangeChanged);
    return () => window.removeEventListener('unifiedAlertsDateRangeChanged', handleDateRangeChanged);
  }, [page, pageSize, searchText, activeTile, sortingInfo, appliedFilters, groupBy, alertCountView]);

  const fetchSummary = async (dateParams?: { startDate?: string; endDate?: string }) => {
    try {
      setSummaryLoading(true);

      const params: any = dateParams ?? getDateParams();

      params.search = searchText || undefined;

      applyAdvanceFilters(params);

      const updatedParams = expandSectorFilterFlat(params, filterOptions);

      const domainInput = updatedParams?.domains;
      const programInput = updatedParams?.program;

      const transformedParams = {
        ...updatedParams,

        ...(domainInput
          ? {
            domain: expandMappedValues(
              domainInput,
              DOMAIN_MAP
            )
          }
          : {}),
        alertCountView,
        ...(programInput
          ? {
            program: expandMappedValues(
              programInput,
              PROGRAM_MAP
            )
          }
          : {})
      };

      const data = await unifiedAlertsService.getSummary(transformedParams);

      setSummary(data);

      return data;

    } catch (error) {
      console.error('Error loading summary:', error);
      return null;
    } finally {
      setSummaryLoading(false);
    }
  };

  const loadSummary = (dateParams?: { startDate?: string; endDate?: string }): Promise<any> => {
    const requestKey = JSON.stringify({
      appliedFilters,
      searchText,
      alertCountView,
      dateRangeType: sessionStorage.getItem('unifiedAlertsDateRangeType'),
      startDate: sessionStorage.getItem('unifiedAlertsStartDate'),
      endDate: sessionStorage.getItem('unifiedAlertsEndDate'),
    });
    if (summaryRequestRef.current?.key === requestKey) return summaryRequestRef.current.promise;

    const promise = fetchSummary(dateParams);
    summaryRequestRef.current = { key: requestKey, promise };
    promise.finally(() => {
      if (summaryRequestRef.current?.promise === promise) summaryRequestRef.current = null;
    });
    return promise;
  };

  const applyAdvanceFilters = (params: any) => {
    if (appliedFilters.severity.length > 0) params.severity = appliedFilters.severity.map((s: any) => s.value).join(',');
    if (appliedFilters.status.length > 0) params.status = appliedFilters.status.map((s: any) => s.value).join(',');
    if (appliedFilters.sector.length > 0) params.sector = appliedFilters.sector.map((s: any) => s.value).join(',');
    if (
      appliedFilters.miscellaneousSector &&
      appliedFilters.miscellaneousSector.length > 0
    ) {
      params.miscellaneousSector =
        appliedFilters.miscellaneousSector.map(
          (s: any) => s.value
        );
    }
    if (appliedFilters.programs.length > 0) params.program = appliedFilters.programs.map((p: any) => p.value).join(',');
    if (appliedFilters.domains.length > 0) params.domains = appliedFilters.domains.map((d: any) => d.value).join(',');
    if (appliedFilters.businessUnits.length > 0) params.businessUnits = appliedFilters.businessUnits.map((b: any) => b.value).join(',');
    if (appliedFilters.sources.length > 0) params.sources = appliedFilters.sources.map((s: any) => s.value).join(',');
    if (appliedFilters.cmdb_ci.length > 0) params.cmdb_ci = appliedFilters.cmdb_ci.map((s: any) => s.value).join(',');
    if (appliedFilters.priority.length > 0) params.priority = appliedFilters.priority.map((s: any) => s.value).join(',');
    if (appliedFilters.assignmentGroup.length > 0) params.assignmentGroup = appliedFilters.assignmentGroup.map((s: any) => s.value).join(',');
    if (appliedFilters.alertCategory && appliedFilters.alertCategory.length > 0) params.alertCategory = appliedFilters.alertCategory.map((s: any) => s.value).join(',');
    if (appliedFilters.department && appliedFilters.department.length > 0) params.department = appliedFilters.department.map((s: any) => s.value).join(',');
    if (appliedFilters.capability && appliedFilters.capability.length > 0) params.capability = appliedFilters.capability.map((s: any) => s.value).join(',');

    if (appliedFilters.isDandAIAG !== undefined && appliedFilters.isDandAIAG !== null) {
      let val: any = appliedFilters.isDandAIAG;
      if (Array.isArray(val)) {
        val = val.length > 0 ? val[0]?.value ?? val[0] : undefined;
      }
      if (val !== undefined && val !== null) {
        if (typeof val === 'boolean') {
          params.isDandAIAG = val;
        } else if (String(val).toLowerCase() === 'yes' || String(val).toLowerCase() === 'true') {
          params.isDandAIAG = true;
        } else if (String(val).toLowerCase() === 'no' || String(val).toLowerCase() === 'false') {
          params.isDandAIAG = false;
        }
      }
    }

    for (let filter of appliedFilters.additionalFilters) {
      if (filter.field === 'sys_created_by') {
        params.sys_created_by = filter.value
      }
      if (filter.field === 'sys_updated_by') {
        params.sys_updated_by = filter.value
      }
      if (filter.field === 'category') {
        params.category = filter.value
      }
      if (filter.field === 'classification') {
        params.classification = filter.value
      }
    }

    if (sortingInfo.sortBy && sortingInfo.sortDir) {
      params.sortBy = sortingInfo.sortBy;
      params.sortOrder = sortingInfo.sortDir;
    }
  }


  const getGroupedValue = (group: any, groupByValue: string) => {
    return group?.[groupByValue] ?? group?.groupValue ?? group?.key ?? group?.value ?? group?.name ?? 'Unassigned';
  };

  const getGroupedCount = (group: any, groupByValue: string) => {
    const countField = `${groupByValue}Count`;
    return group?.[countField] ?? group?.totalChildrenCount ?? group?.totalChildren ?? group?.totalCount ?? group?.unique_alerts_count ?? group?.count ?? group?.children?.length ?? 0;
  };

  const normalizeGroupedRows = (groups: any[], groupByValue: string) => {
    return (groups || []).map((group: any) => {
      const groupValue = getGroupedValue(group, groupByValue);
      const totalCount = getGroupedCount(group, groupByValue);
      const countField = `${groupByValue}Count`;

      return {
        ...group,
        [groupByValue]: groupValue,
        [countField]: group?.[countField] ?? totalCount,
        children: Array.isArray(group?.children) ? group.children : [],
        totalCount,
        totalChildrenCount: group?.totalChildrenCount ?? group?.totalChildren ?? totalCount,
        isParent: true,
      };
    });
  };

  const loadMoreChildren = useCallback(async (groupByValue: string, groupValue: string, currentCount: number, parentRow: any) => {
    setIsLoadingMoreChildren(true);
    setLoadingGroupId(`${groupByValue}-${groupValue}`);

    try {
      const params: any = {
        pageSize: 50,
        groupBy: groupByValue,
        groupValue,
        afterCount: currentCount,
        search: searchText || undefined,
        ...(!ignoreDate
          ? getDatePayloadParams()
          : {}),
        ...(columnFiltersRef.current.length > 0 && { columnFilters: columnFiltersRef.current }),
      };

      applyAdvanceFilters(params);
      if (activeTile !== 'Closed') {
        params.severity = activeTile;
      } else {
        params.status = 'Closed'
      }
      const updatedParams = expandSectorFilterFlat(params, filterOptions);

      const domainInput = updatedParams?.domains;
      const programInput = updatedParams?.program;

      const transformedParams = {
        ...updatedParams,

        ...(domainInput
          ? {
            domain: expandMappedValues(
              domainInput,
              DOMAIN_MAP
            )
          }
          : {}),
        alertCountView,
        ...(programInput
          ? {
            program: expandMappedValues(
              programInput,
              PROGRAM_MAP
            )
          }
          : {})
      };

      const response: any = await unifiedAlertsService.getAlertsGroupedChildren(transformedParams);
      const responseData = response?.data ?? response;

      const responseGroups =
        responseData?.data?.groups ||
        responseData?.groups ||
        response?.groups ||
        responseData?.data?.children ||
        response?.data?.children ||
        [];

      let matchedGroup: any = null;

      if (Array.isArray(responseGroups) && responseGroups.length > 0) {
        matchedGroup = responseGroups.find((group: any) => {
          const value =
            group?.[groupByValue] ??
            group?.groupValue ??
            group?.key ??
            group?.value ??
            group?.name;

          return String(value ?? '').trim() === String(groupValue ?? '').trim();
        });
      }

      const rawChildren =
        responseData?.children ||
        response?.children ||
        responseData?.data?.children ||
        matchedGroup?.children ||
        [];

      const children = Array.isArray(rawChildren) ? rawChildren : [];
      const totalChildren =
        responseData?.totalChildrenCount ??
        response?.totalChildrenCount ??
        matchedGroup?.totalChildrenCount ??
        matchedGroup?.totalChildren ??
        matchedGroup?.[`${groupByValue}Count`] ??
        parentRow?.totalChildrenCount ??
        parentRow?.totalChildren ??
        parentRow?.[`${groupByValue}Count`] ??
        parentRow?.count ??
        responseData?.totalCount ??
        response?.totalCount ??
        children.length;

      const nextOffset =
        responseData?.nextOffset ??
        response?.nextOffset ??
        matchedGroup?.nextOffset ??
        currentCount + children.length;

      const hasMore =
        responseData?.hasMore ??
        response?.hasMore ??
        matchedGroup?.hasMore ??
        matchedGroup?.hasMoreChildren ??
        (typeof totalChildren === 'number' ? nextOffset < totalChildren : false);

      return {
        children,
        hasMore,
        nextOffset,
        totalChildrenCount: totalChildren,
      };
    } catch (error) {
      console.error('Error loading more children:', error);
      return null;
    } finally {
      setIsLoadingMoreChildren(false);
      setLoadingGroupId(null);
    }
  }, [activeTile, appliedFilters, filterOptions, searchText, sortingInfo, fixedStartDate, fixedEndDate]);

  const loadAlerts = async (dateParams?: { startDate?: string; endDate?: string }) => {
    const isListView = !groupBy || groupBy === 'none';
    const alertPageSignature = JSON.stringify({
      pageSize,
      activeTile,
      sortingInfo,
      appliedFilters,
      searchText,
      alertCountView,
      ignoreDate,
      columnFilters: columnFiltersRef.current,
      dateRangeType: sessionStorage.getItem('unifiedAlertsDateRangeType'),
    });
    const alertPageRequest = isListView
      ? getAlertPageRequest(alertPageCursorRef.current, alertPageSignature, page)
      : null;

    if (isListView && !alertPageRequest) {
      // The page can't be reached with the current cursor; setPage re-runs the load from page one
      setPage(1);
      return;
    }

    loadSummary(dateParams);

    setIsLoading(true);
    try {
      let searchNext = null;

      if (groupBy && groupBy !== 'none' && page > 1) {
        const afterKey = searchAfter;
        if (afterKey && typeof afterKey === 'object' && Object.keys(afterKey).length > 0) {
          searchNext = afterKey;
        }
      }

      const params: any = {
        page,
        pageSize,
        search: searchText || undefined,
        ...(!ignoreDate
          ? getDatePayloadParams(dateParams)
          : {}),
        ...(searchNext && { searchNext }),
        ...(groupBy && groupBy !== 'none' && { groupBy }),
        ...(columnFiltersRef.current.length > 0 && { columnFilters: columnFiltersRef.current }),
        ...alertPageRequest,
      };

      applyAdvanceFilters(params)
      if (activeTile !== 'Closed') {
        params.severity = activeTile;
      } else {
        params.status = 'Closed'
      }

      const updatedParams = expandSectorFilterFlat(params, filterOptions);
      const domainInput = updatedParams?.domains;
      const programInput = updatedParams?.program;

      const transformedParams = {
        ...updatedParams,

        ...(domainInput
          ? {
            domain: expandMappedValues(
              domainInput,
              DOMAIN_MAP
            )
          }
          : {}),
        alertCountView,
        ...(programInput
          ? {
            program: expandMappedValues(
              programInput,
              PROGRAM_MAP
            )
          }
          : {})
      };

      const response: any = await unifiedAlertsService.getAlerts(transformedParams);
      const responseData = response?.data ?? response;
      const groupedResponse =
        response?.isGrouped === true ||
        responseData?.isGrouped === true ||
        Boolean(groupBy && groupBy !== 'none' && (responseData?.data?.groups || responseData?.groups || response?.groups));

      if (groupedResponse) {
        const groups = responseData?.data?.groups || responseData?.groups || response?.groups || [];
        const normalizedGroups = normalizeGroupedRows(groups, groupBy);
        setAlerts(normalizedGroups);
        setTotalAlerts(
          responseData?.totalGroups ??
          responseData?.pagination?.totalGroups ??
          responseData?.pagination?.total ??
          response?.pagination?.totalGroups ??
          response?.pagination?.total ??
          normalizedGroups.length
        );
        setSearchAfter(responseData?.searchNext || response?.searchNext || null);
        alertPageCursorRef.current = null;
        setIsGrouped(true);
        setIsDataOpsAdmin(response?.isDataOpsAdmin);
      } else {
        const rows = Array.isArray(responseData) ? responseData : responseData?.data ?? [];
        for (let row of rows) {
          if (!row.cmdb_ci) {
            row.cmdb_ci = 'CI not available';
          }
        }
        setAlerts(rows);
        setTotalAlerts(response?.pagination?.total ?? responseData?.pagination?.total ?? responseData?.total ?? rows.length);
        setSearchAfter(response?.searchNext || responseData?.searchNext || null);
        const pagination = response?.pagination ?? responseData?.pagination;
        alertPageCursorRef.current = alertPageRequest && {
          signature: alertPageSignature,
          page,
          firstSortValues: pagination?.firstSortValues ?? null,
          lastSortValues: pagination?.lastSortValues ?? null,
          request: alertPageRequest,
        };
        setIsGrouped(false);
        setIsDataOpsAdmin(response?.isDataOpsAdmin);
      }
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTrendData = async (summaryData: any, dateParams?: { startDate?: string; endDate?: string }) => {
    try {
      setIsChartsLoading(true);
      const params = {
        ...getDatePayloadParams(dateParams),
      }
      applyAdvanceFilters(params)
      const updatedParams = expandSectorFilterFlat(params, filterOptions);

      const domainInput = updatedParams?.domains;
      const programInput = updatedParams?.program;

      const transformedParams = {
        ...updatedParams,

        ...(domainInput
          ? {
            domain: expandMappedValues(
              domainInput,
              DOMAIN_MAP
            )
          }
          : {}),
        alertCountView,
        ...(programInput
          ? {
            program: expandMappedValues(
              programInput,
              PROGRAM_MAP
            )
          }
          : {})
      };

      const [sectorData, programData] = await Promise.all([
        unifiedAlertsService.getTrendBySector(transformedParams),
        unifiedAlertsService.getTrendByProgram(transformedParams),
      ]);

      const severities = appliedFilters.severity.length > 0 ? appliedFilters.severity.map(i => i.value.toUpperCase()) : ['CRITICAL', 'MAJOR', 'MINOR', 'WARNING'];

      const totalAlerts = severities.reduce((sum, severity) => {
        return sum + (summaryData?.[severity]?.total || 0);
      }, 0);

      const previousHourTotal = severities.reduce((sum, severity) => {
        return sum + (summaryData?.[severity]?.previousHour || 0);
      }, 0);

      let avgTrend = 'same';

      if (totalAlerts > previousHourTotal) {
        avgTrend = 'up';
      } else if (totalAlerts < previousHourTotal) {
        avgTrend = 'down';
      }

      const rawTrendData = transformTrendData(sectorData as any[], 'sectors');
      const rawProData = transformTrendData(programData as any[], 'program');

      const trendData = {
        ...rawTrendData,
        stats: {
          ...rawTrendData.stats,
          totalAlerts
        }
      };

      const proData = {
        ...rawProData,
        stats: {
          ...rawProData.stats,
          totalAlerts,
          avgTrend
        }
      };

      setTrendSector(trendData);
      setTrendProgram(proData);

    } catch (error) {
      console.error('Error loading trend data:', error);
    } finally {
      setIsChartsLoading(false);
    }
  };

  const loadSources = async (dateParams?: { startDate?: string; endDate?: string }) => {
    try {
      const params = {
        ...getDatePayloadParams(dateParams),
      }
      applyAdvanceFilters(params)
      const updatedParams = expandSectorFilterFlat(params, filterOptions);

      const domainInput = updatedParams?.domains;
      const programInput = updatedParams?.program;

      const transformedParams = {
        ...updatedParams,

        ...(domainInput
          ? {
            domain: expandMappedValues(
              domainInput,
              DOMAIN_MAP
            )
          }
          : {}),
        alertCountView,
        ...(programInput
          ? {
            program: expandMappedValues(
              programInput,
              PROGRAM_MAP
            )
          }
          : {})
      };


      const data = await unifiedAlertsService.getSources(transformedParams);
      const total = data.reduce((s: number, d: any) => s + d.count, 0);
      setSources(data.map((d: any) => ({ ...d, percentage: total > 0 ? parseFloat(((d.count / total) * 100).toFixed(1)) : 0 })));
    } catch (error) {
      console.error('Error loading sources:', error);
    }
  };

  const loadOpenPieData = async (dateParams?: { startDate?: string; endDate?: string }) => {
    try {
      const params = {
        ...getDatePayloadParams(dateParams),
      }
      applyAdvanceFilters(params)
      const updatedParams = expandSectorFilterFlat(params, filterOptions);

      const domainInput = updatedParams?.domains;
      const programInput = updatedParams?.program;

      const transformedParams = {
        ...updatedParams,

        ...(domainInput
          ? {
            domain: expandMappedValues(
              domainInput,
              DOMAIN_MAP
            )
          }
          : {}),
        alertCountView,
        ...(programInput
          ? {
            program: expandMappedValues(
              programInput,
              PROGRAM_MAP
            )
          }
          : {})
      };


      const data = await unifiedAlertsService.getPieData(transformedParams);
      setSeverityData(data)
    } catch (error) {
      console.error('Error loading sources:', error);
    }
  };

  const loadMostImpactedApps = async (dateParams?: { startDate?: string; endDate?: string }) => {
    try {
      const params = {
        ...getDatePayloadParams(dateParams),
      }
      applyAdvanceFilters(params)
      const updatedParams = expandSectorFilterFlat(params, filterOptions);

      const domainInput = updatedParams?.domains;
      const programInput = updatedParams?.program;

      const transformedParams = {
        ...updatedParams,

        ...(domainInput
          ? {
            domain: expandMappedValues(
              domainInput,
              DOMAIN_MAP
            )
          }
          : {}),
        alertCountView,
        ...(programInput
          ? {
            program: expandMappedValues(
              programInput,
              PROGRAM_MAP
            )
          }
          : {})
      };


      const data = await unifiedAlertsService.getMostImpactedApps(transformedParams);
      setMostImpactedApps(data);
    } catch (error) {
      console.error('Error loading most impacted apps:', error);
    }
  };

  const loadAllDropDowns = async () => {
    try {
      const response = await unifiedAlertsService.getDropDownData();
      const { fullMap, ...data } = response;

      const miscellaneousSectors = [
        "Russia",
        "AMESA APAC",
        "CTO",
        "DPS",
        "EXCASA",
        "GBS",
        "PBF",
        "PMF",
        "Quaker",
        "RnD",
        "SnT",
        "Soda Stream"
      ];
      data.sector = data.sector.filter(
        (sector: string) => !miscellaneousSectors.includes(sector)
      );

      data.sector.push('Miscellaneous');

      data.sectorDropdownValues = {
        ...data.sectorDropdownValues,
        Miscellaneous: miscellaneousSectors
      };

      const relationshipRecords = Array.from(
        new Map(
          Object.values(fullMap || {})
            .flatMap((fieldMap: any) =>
              Object.values(fieldMap || {})
                .flatMap((records: any) => records || [])
            )
            .map((record: any) => [
              JSON.stringify(record),
              record
            ])
        ).values()
      );
      setFilterRelationshipRecords(relationshipRecords || []);

      const newFilterOptions = { ...DEFAULT_FILTER_OPTIONS, ...data };
      setFilterOptions(newFilterOptions);
      filterOptionsRef.current = newFilterOptions;
    } catch (error) {
      console.error('Error loading most impacted apps:', error);
    }
  };

  const loadAll = async (dateParams?: { startDate?: string; endDate?: string }) => {
    setFixedStartDate(dateParams?.startDate)
    setFixedEndDate(dateParams?.endDate)

    const summaryData = await loadSummary(dateParams);

    if (summaryData) {
      loadTrendData(summaryData, dateParams);
    }

    loadSources(dateParams);
    loadMostImpactedApps(dateParams);
    loadOpenPieData(dateParams)
  };

  useEffect(() => {
    const handleStorageChange = () => {
      const saved = sessionStorage.getItem('unifiedAlertsAutoRefresh');
      if (saved !== null) setAutoRefreshInterval(saved === '0' ? null : Number(saved));
    };

    window.addEventListener('storage', handleStorageChange);
    const checkInterval = setInterval(() => {
      const saved = sessionStorage.getItem('unifiedAlertsAutoRefresh');
      const current = autoRefreshInterval === null ? 0 : autoRefreshInterval;
      if (saved !== null && Number(saved) !== current) {
        setAutoRefreshInterval(saved === '0' ? null : Number(saved));
      }
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(checkInterval);
    };
  }, [autoRefreshInterval]);

  useEffect(() => {
    const handleAlertsSearch = (event: any) => {
      const searchText = (event.detail?.searchText || '').trim();
      setSimpleSearchText(searchText);
      setSearchText(searchText);
      setPage(1);
    };

    window.addEventListener('alertsSearch', handleAlertsSearch);
    return () => window.removeEventListener('alertsSearch', handleAlertsSearch);
  }, []);

  const loadSavedFilters = async () => {
    try {
      let savedFilters = await loadDashboardFilters({
        dashboardId: 'snow',
        dashboardName: 'Snow Alerts Dashboard',
        pageName: 'Snow Alerts',
      }).catch(() => null);

      if (!savedFilters?.filters) {
        const localSaved = localStorage.getItem('unifiedAlerts.defaultFilters');
        if (localSaved) {
          try {
            savedFilters = JSON.parse(localSaved);
          } catch (e) {
            savedFilters = null;
          }
        }
      }

      if (savedFilters?.filters) {
        const { savedActiveTile } = savedFilters?.meta_data || {};

        const hydratedFilters = savedFilters.filters as FilterState;

        setAppliedFilters(hydratedFilters);
        setActiveTile(savedActiveTile || 'Critical');

        persistDateRangeToSession(hydratedFilters);

        window.dispatchEvent(new Event('unifiedAlertsDateRangeHydrated'));
      } else {
        const currentOptions = filterOptionsRef.current?.sector?.length ? filterOptionsRef.current : filterOptions;
        const defaultSectors = (currentOptions?.sector || []).map((s: any) => ({
          label: s,
          value: s,
        }));
        const defaultMisc = (currentOptions?.sectorDropdownValues?.Miscellaneous || []).map((v: string) => ({
          label: v,
          value: v,
        }));

        setAppliedFilters({
          ...DEFAULT_FILTERS,
          sector: defaultSectors,
          miscellaneousSector: defaultMisc,
        });
        setActiveTile('Critical');
        sessionStorage.setItem('unifiedAlertsDateRangeType', 'last_15_minutes');
        window.dispatchEvent(new Event('unifiedAlertsDateRangeHydrated'));
      }
    } catch (e) {
      console.error('Failed to load saved filters', e);
    } finally {
      setIsFiltersHydrated(true);
    }
  };

  useEffect(() => {
    if (initializationStartedRef.current) return;
    initializationStartedRef.current = true;

    const init = async () => {
      await loadAllDropDowns();
      await loadSavedFilters();
    };
    init();
  }, []);

  useEffect(() => {
    if (!isFiltersHydrated) return;

    loadAll(getDateParams());
  }, [isFiltersHydrated, appliedFilters, searchText, alertCountView]);

  useEffect(() => {
    if (!isFiltersHydrated) return;

    loadAlerts(getDateParams());
  }, [
    isFiltersHydrated,
    appliedFilters,
    searchText,
    alertCountView,
    page,
    pageSize,
    activeTile,
    sortingInfo,
    groupBy,
    ignoreDate,
    columnFilters
  ]);

  useEffect(() => {
    setPage(1);
    setSearchAfter(null);
  }, [groupBy]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefreshInterval) {
      intervalRef.current = setInterval(() => {
        loadAll(getDateParams());
        loadAlerts(getDateParams());
      }, autoRefreshInterval * 60 * 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefreshInterval, page, pageSize, searchText, activeTile, sortingInfo, appliedFilters, groupBy, alertCountView]);

  const handleTileClick = (key: string) => {
    setActiveTile(key);
    setPage(1);
  };

  const handleApplyFilters = async (filters: FilterState) => {
    const filtersToApply = withCustomDates(filters);

    setAppliedFilters(filtersToApply);
    persistDateRangeToSession(filtersToApply);
    setPage(1);
  };

  const handleSaveDefaultFilters = async (filters: FilterState) => {
    const filtersToSave = withCustomDates(filters);

    setAppliedFilters(filtersToSave);
    persistDateRangeToSession(filtersToSave);

    localStorage.setItem(
      'unifiedAlerts.defaultFilters',
      JSON.stringify({
        filters: filtersToSave,
        meta_data: {
          savedActiveTile: activeTile,
        }
      })
    );

    await saveDashboardFilters({
      dashboardId: 'snow',
      dashboardName: 'Snow Alerts Dashboard',
      pageName: 'Snow Alerts',
      meta_data: {
        savedActiveTile: activeTile,
      }
    }, filtersToSave).catch(() => { });

    setPage(1);
  };


  const handleResetDefaultFilters = async () => {
    await loadSavedFilters();
    setPage(1);
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    const minDelay = new Promise((resolve) => setTimeout(resolve, 2000));
    try {
      const params: any = {
        page: 1,
        pageSize: 200000,
        search: searchText || undefined
      };

      if (appliedFilters.severity.length > 0) params.severity = appliedFilters.severity.map((s: any) => s.value).join(',');
      if (appliedFilters.status.length > 0) params.status = appliedFilters.status.map((s: any) => s.value).join(',');
      if (appliedFilters.sector.length > 0) params.sector = appliedFilters.sector.map((s: any) => s.value).join(',');
      if (appliedFilters.programs.length > 0) params.program = appliedFilters.programs.map((p: any) => p.value).join(',');
      if (appliedFilters.domains.length > 0) params.domains = appliedFilters.domains.map((d: any) => d.value).join(',');
      if (appliedFilters.businessUnits.length > 0) params.businessUnits = appliedFilters.businessUnits.map((b: any) => b.value).join(',');
      if (appliedFilters.sources.length > 0) params.sources = appliedFilters.sources.map((s: any) => s.value).join(',');
      if (appliedFilters.cmdb_ci.length > 0) params.cmdb_ci = appliedFilters.cmdb_ci.map((s: any) => s.value).join(',');
      if (appliedFilters.priority.length > 0) params.priority = appliedFilters.priority.map((s: any) => s.value).join(',');
      if (appliedFilters.assignmentGroup.length > 0) params.assignmentGroup = appliedFilters.assignmentGroup.map((s: any) => s.value).join(',');
      if (appliedFilters.alertCategory && appliedFilters.alertCategory.length > 0) params.alertCategory = appliedFilters.alertCategory.map((s: any) => s.value).join(',');
      if (appliedFilters.timeRange && !ignoreDate) {
        Object.assign(params, getDatePayloadParams());
      }
      if (sortingInfo.sortBy && sortingInfo.sortDir) {
        params.sortBy = sortingInfo.sortBy;
        params.sortOrder = sortingInfo.sortDir;
      }
      if (activeTile !== 'Closed') {
        params.severity = activeTile;
      } else {
        params.status = 'Closed'
      }
      if (groupBy && groupBy !== 'none') {
        params.groupBy = groupBy;
      }
      if (columnFiltersRef.current.length > 0) {
        params.columnFilters = columnFiltersRef.current;
      }

      const updatedParams = expandSectorFilterFlat(params, filterOptions);
      const domainInput = updatedParams?.domains;
      const programInput = updatedParams?.program;

      const transformedParams = {
        ...updatedParams,

        ...(domainInput
          ? {
            domain: expandMappedValues(
              domainInput,
              DOMAIN_MAP
            )
          }
          : {}),
        alertCountView,
        ...(programInput
          ? {
            program: expandMappedValues(
              programInput,
              PROGRAM_MAP
            )
          }
          : {})
      };
      await Promise.all([unifiedAlertsService.exportAlerts(transformedParams), minDelay]);
    } catch (error) {
      console.error('Error exporting alerts:', error);
    } finally {
      setIsExporting(false);
    }
  };

  /** Fetch up to 500 rows for PDF export (respects all current filters). */
  const fetchPdfData = async (): Promise<any[]> => {
    try {
      const params: any = {
        page: 1,
        pageSize: 500,
        search: searchText || undefined,
        ...(!ignoreDate
          ? getDatePayloadParams()
          : {}),
      };

      applyAdvanceFilters(params);

      if (activeTile !== 'Closed') {
        params.severity = activeTile;
      } else {
        params.status = 'Closed';
      }
      if (columnFiltersRef.current.length > 0) {
        params.columnFilters = columnFiltersRef.current;
      }

      const domainInput = params?.domain;
      const programInput = params?.program;

      const transformedParams = {
        ...params,

        ...(domainInput
          ? {
            domain: expandMappedValues(
              domainInput,
              DOMAIN_MAP
            )
          }
          : {}),
        alertCountView,
        ...(programInput
          ? {
            program: expandMappedValues(
              programInput,
              PROGRAM_MAP
            )
          }
          : {})
      };
      const response = await unifiedAlertsService.getAlerts(transformedParams);
      return response?.data || [];
    } catch (error) {
      console.error('Error fetching PDF data:', error);
      return [];
    }
  };

  const handleColumnFilterChange = useCallback((payload: ColumnFilterPayloadItem[], sortModel: SortModel) => {
    saveColumnFilterPayload(EWS_COLUMN_FILTER_STORAGE_KEY, payload);
    setColumnFilters(payload);
    setSortingInfo(getSortingFromSortModel(sortModel, DEFAULT_ALERT_SORTING));
    setPage(1);
  }, []);


  const getColumnFilterValues = useCallback(async (field: string): Promise<any[]> => {
    if (field === 'assignment_group') {
      return (filterOptions?.assignmentGroup ?? [])
        .map((group: any) => (typeof group === 'string' ? group : group?.assignmentGroup))
        .filter(Boolean);
    }
    const values = filterOptions?.[EWS_COLUMN_FILTER_OPTION_KEYS[field] ?? field];
    if (Array.isArray(values)) {
      return values.filter(
        (v: any) =>
          v !== null &&
          v !== undefined &&
          String(v).trim() !== '' &&
          String(v).trim().toLowerCase() !== '(blank)'
      );
    }
    if (field === 'isDandAIAG') return ['Yes', 'No'];
    return field === 'dataQuality' ? DATA_QUALITY_COLUMN_FILTER_VALUES : [];
  }, [filterOptions]);

  return {
    activeTile,
    setActiveTile,
    page,
    pageSize,
    searchText,
    simpleSearchText,
    sortingInfo,
    appliedFilters,
    summary,
    alerts,
    isDataOpsAdmin,
    totalAlerts,
    trendSector,
    trendProgram,
    sources,
    mostImpactedApps,
    severityData,
    isLoading,
    isChartsLoading,
    isExporting,
    filterOptions,
    fixedStartDate,
    fixedEndDate,
    setFixedStartDate,
    setFixedEndDate,
    setPage,
    setPageSize,
    setSearchText,
    setSimpleSearchText,
    setSortingInfo,
    handleTileClick,
    handleApplyFilters,
    handleSaveDefaultFilters,
    handleResetDefaultFilters,
    handleExport,
    fetchPdfData,
    setGroupBy,
    groupBy,
    isGrouped,
    setIsGrouped,
    searchAfter,
    setSearchAfter,
    loadMoreChildren,
    isLoadingMoreChildren,
    loadingGroupId,
    setLoadingGroupId,
    summaryLoading,
    expandSectorFilterFlat,
    expandMappedValues,
    filterRelationshipRecords,
    alertCountView,
    setAlertCountView,
    columnFilters,
    handleColumnFilterChange,
    getColumnFilterValues
  };
};
